import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'
import { doc, Firestore, updateDoc } from '@angular/fire/firestore'
import { HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import {
    Client,
    ContractStatus,
    CreatePaymentMethodRequest,
    CreatePaymentMethodResponse,
    FollowupRequestRequest,
    FollowupRequestResponse,
    PayGateCard,
    PayGateClient,
    PayGateMpiResponse,
    PayGateResultCode,
    PaymentMethod,
    paymentsPath,
    ProcessTokenPaymentRequest,
    ProcessTokenPaymentResponse,
    SettlementRequestRequest,
    SlackChannel,
    Subscription,
    subscriptionsPath,
} from '@parkupp/core'
import { AuthenticationService } from './authentication.service'
import { firstValueFrom } from 'rxjs'
import { BackendService } from './backend.service'

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private urlCreatePaymentMethod = `${environment.functionsUrl}/payGate-httpCreatePaymentMethod`
    private urlProcessTokenPayment = `${environment.functionsUrl}/payGate-httpProcessTokenPayment`
    private httpSettlementRequestUrl = `${environment.functionsUrl}/payGate-httpSettlementRequest`
    private urlFollowupRequest = `${environment.functionsUrl}/payGate-httpFollowupRequest`
    private urlVoidRequest = `${environment.functionsUrl}/payGate-httpVoidRequest`

    constructor(private authSvc: AuthenticationService, public firestore: Firestore, private router: Router, private http: HttpClient, private backendService: BackendService) {}

    httpCreatePaymentMethod(subscriptionKey: string, payGateCard: PayGateCard, client: Client) {
        return new Promise<any>((resolve) => {
            let payGateClient = new PayGateClient({
                firstName: client.firstName,
                lastName: client.lastName,
                email: client.$email,
                mobile: client.$phoneNumber,
                key: client.$key,
            })

            let createPaymentMethodRequest = new CreatePaymentMethodRequest({
                subscriptionKey: subscriptionKey,
                card: payGateCard,
                payGateClient: payGateClient,
            })

            this.http.post<CreatePaymentMethodResponse>(this.urlCreatePaymentMethod, createPaymentMethodRequest).subscribe((response: CreatePaymentMethodResponse) => {
                resolve(response)
            })
        })
    }

    async httpProcessTokenPaymentCreateSubscription(selectedPaymentMethod: PaymentMethod, subscriptionKey: string): Promise<ProcessTokenPaymentResponse | { error: boolean }> {
        // Create the request payload
        let post = new ProcessTokenPaymentRequest({
            paymentMethodKey: selectedPaymentMethod.$key!,
            subscriptionKey: subscriptionKey,
        })

        try {
            // Convert Observable to Promise using firstValueFrom
            const response: ProcessTokenPaymentResponse = await firstValueFrom(this.http.post<ProcessTokenPaymentResponse>(this.urlProcessTokenPayment, post))
            console.log('ProcessTokenPaymentResponse', response)
            // Check the response and return accordingly
            if (response) {
                if (!response.error && response.payGateResponse.resultCode == PayGateResultCode['Auth Done']) {
                    return response
                } else {
                    // Log error if payGateResponse contains an error
                    console.log('Should not get here', response)
                    return response
                }
            } else {
                // If response or payGateResponse is not available, return an error object
                return { error: true }
            }
        } catch (error) {
            // Log and rethrow or handle errors from HTTP request
            console.error('HTTP request failed', error)
            return { error: true }
        }
    }

    httpFollowupRequest(activeClient: Client, payGateMpiResponse: PayGateMpiResponse) {
        return new Promise<any>((resolve) => {
            let payGateClient = new PayGateClient({
                firstName: activeClient.firstName,
                lastName: activeClient.lastName,
                email: activeClient.$email,
                mobile: activeClient.$phoneNumber,
                key: activeClient.$key,
            })

            let post = new FollowupRequestRequest({
                payGateClient: payGateClient,
                payRequestId: payGateMpiResponse.payRequestId,
            })

            this.http.post<FollowupRequestResponse>(this.urlFollowupRequest, post).subscribe((response: FollowupRequestResponse) => {
                resolve(response)
            })
        })
    }

    httpApproveRequest(subscription: Subscription, paymentKey: string) {
        return new Promise<any>((resolve) => {
            //TODO: This will break if there are 2 payments on the sub
            let post = new SettlementRequestRequest({
                paymentKey: paymentKey,
            })

            this.http.post<SettlementRequestRequest>(this.httpSettlementRequestUrl, post).subscribe((response: SettlementRequestRequest) => {
                resolve(response)
            })
        })
    }

    async clientCancelSubscription(subscription: Subscription) {
        await this.backendService.httpSendSlack({ message: `*Subscription Cancelled* - By Client: ${subscription.denormClientFirstName} ${subscription.denormClientLastName}` })

        const paymentKey = subscription.paymentRefs[0].id as string

        const response = await this.backendService.httpVoidRequest(subscription.$key!)

        if (!response.error) {
            // Update Payment
            await updateDoc(doc(this.firestore, paymentsPath(), paymentKey), {
                voided: true,
            })
        } else {
            await this.backendService.httpCreateException({ message: `*Failed to reject or void the payment*`, json: { paymentKey } })
        }

        // Update subscription
        await updateDoc(doc(this.firestore, subscriptionsPath(), subscription.$key!), {
            status: ContractStatus.clientCancelled,
        })

        return
    }
}
